import * as Styled from './PunchupSendTicketsFormSeated.styles';

import { useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { X } from 'lucide-react';
import type { EventManager, SeatingChart, SelectableObject } from '@seatsio/seatsio-types';

import { FlexContainer } from 'features/common/components/FlexContainer';
import { PunchupBuyerSeatsWidget } from 'features/common/components/Punchup/composites/PunchupBuyerSeatsWidget';
import { PunchupButton } from 'features/common/components/Form/PunchupButton/PunchupButton';
import { PunchupInput } from 'features/common/components/Form/PunchupInput';
import { PunchupInputToggle } from 'features/common/components/Form/PunchupInputToggle/PunchupInputToggle';
import { PunchupTag } from 'features/common/components/Punchup/primitives/PunchupTag';
import { RecipientProps, RecipientTicketType, SendTicketsEventProps } from 'features/SendTickets/types';
import { initialRecipient } from 'features/SendTickets/utils';

interface Props {
	event: SendTicketsEventProps;
}

const getLabel = (obj: SelectableObject): string | null => {
	const label = (obj as { label?: string }).label;
	return label ? String(label) : null;
};

const getCategoryKey = (obj: SelectableObject): string | null => {
	const key = (obj as { category?: { key?: string | number } }).category?.key;
	return key === undefined || key === null ? null : String(key);
};

export const PunchupSendTicketsFormSeated = ({ event }: Props) => {
	const { getValues, setValue, watch } = useFormContext();
	const recipients: RecipientProps[] = watch('recipients') ?? [];

	const [activeRecipientId, setActiveRecipientId] = useState(0);
	const chartRef = useRef<SeatingChart | null>(null);

	const categoryToTicketType = useMemo(() => {
		const map = new Map<string, RecipientTicketType>();
		event.ticketTypes.forEach((tt) => {
			if (tt.seatsCategoryKey) map.set(String(tt.seatsCategoryKey), tt);
		});
		return map;
	}, [event.ticketTypes]);

	const ticketTypeId = (tt: RecipientTicketType) => tt.ticket_type_id ?? tt.ticketTypeId;

	const onChartRendered = useCallback((chart: EventManager) => {
		chartRef.current = chart as unknown as SeatingChart;
	}, []);

	const onObjectSelected = useCallback(
		(object: SelectableObject) => {
			const label = getLabel(object);
			const categoryKey = getCategoryKey(object);
			if (!label || !categoryKey) return;
			const tt = categoryToTicketType.get(categoryKey);
			if (!tt) return;

			const current = (getValues('recipients') as RecipientProps[]) ?? [];
			const recipient = current[activeRecipientId];
			if (!recipient) return;

			const entries = [...recipient.ticketTypes];
			const idx = entries.findIndex((e) => ticketTypeId(e) === ticketTypeId(tt));
			if (idx === -1) {
				entries.push({ ...tt, quantity: 1, seats: [label] });
			} else {
				const existing = entries[idx];
				const seats = existing.seats ? [...existing.seats, label] : [label];
				entries[idx] = { ...existing, seats, quantity: seats.length };
			}
			const next = [...current];
			next[activeRecipientId] = { ...recipient, ticketTypes: entries };
			setValue('recipients', next, { shouldDirty: true });
		},
		[activeRecipientId, categoryToTicketType, getValues, setValue]
	);

	const onObjectDeselected = useCallback(
		(object: SelectableObject) => {
			const label = getLabel(object);
			if (!label) return;
			const current = (getValues('recipients') as RecipientProps[]) ?? [];
			let touched = false;
			const next = current.map((recipient) => {
				const entries = recipient.ticketTypes
					.map((tt) => {
						if (!tt.seats?.includes(label)) return tt;
						touched = true;
						const seats = tt.seats.filter((s) => s !== label);
						return { ...tt, seats, quantity: seats.length };
					})
					.filter((tt) => (tt.seats?.length ?? 0) > 0);
				return { ...recipient, ticketTypes: entries };
			});
			if (touched) setValue('recipients', next, { shouldDirty: true });
		},
		[getValues, setValue]
	);

	const handleAddRecipient = () => {
		const current = (getValues('recipients') as RecipientProps[]) ?? [];
		setValue('recipients', [...current, initialRecipient]);
		setActiveRecipientId(current.length);
	};

	const handleRemoveRecipient = (id: number) => {
		const current = (getValues('recipients') as RecipientProps[]) ?? [];
		const recipient = current[id];
		if (!recipient) return;
		const seats: string[] = [];
		recipient.ticketTypes.forEach((tt) => {
			if (tt.seats) seats.push(...tt.seats);
		});
		// Drop the recipient first so onObjectDeselected (fired by the chart in response
		// to deselectObjects below) finds no owner and is a no-op — no second getValues,
		// no chance of operating on stale state across the async boundary.
		const next = [...current];
		next.splice(id, 1);
		setValue('recipients', next);
		setActiveRecipientId((curr) => {
			if (curr === id) return 0;
			if (curr > id) return curr - 1;
			return curr;
		});
		if (chartRef.current && seats.length > 0) {
			chartRef.current.deselectObjects(seats).catch(() => {
				// Recipient is already gone locally; chart will eventually reconcile.
			});
		}
	};

	const deselectSeat = async (label: string) => {
		if (!chartRef.current) return;
		try {
			await chartRef.current.deselectObjects([label]);
		} catch {
			// fall through
		}
	};

	return (
		<>
			<Styled.ActiveRecipientRow>
				<Styled.ActiveRecipientLabel htmlFor="active-recipient">Assigning seats to</Styled.ActiveRecipientLabel>
				<Styled.DropdownWrap>
					<Styled.ActiveRecipientSelect
						id="active-recipient"
						value={activeRecipientId}
						onChange={(e) => setActiveRecipientId(Number(e.target.value))}
					>
						{recipients.map((_, i) => (
							<option key={i} value={i}>
								Recipient {i + 1}
							</option>
						))}
					</Styled.ActiveRecipientSelect>
				</Styled.DropdownWrap>
			</Styled.ActiveRecipientRow>

			{/* No session: a hold would block its own comp — the comp route books without a hold token, and a
			    GA area's last places cannot be booked while held (TIC-882). Green Send Tickets holds nothing too. */}
			<PunchupBuyerSeatsWidget
				eventKey={event.seatsEventKey}
				chartProps={{ onChartRendered, onObjectSelected, onObjectDeselected }}
			/>

			{recipients.map((recipient, i) => {
				const assignedSeats: { label: string; ticketTypeName: string }[] = [];
				recipient.ticketTypes.forEach((tt) => {
					(tt.seats ?? []).forEach((label) => assignedSeats.push({ label, ticketTypeName: tt.type }));
				});
				const firstnameId = `recipients[${i}].firstName`;
				const lastnameId = `recipients[${i}].lastName`;
				const emailId = `recipients[${i}].email`;
				const noEmailId = `recipients[${i}].noEmail`;
				const noEmail = !!recipient.noEmail;
				return (
					<Styled.RecipientCard key={i}>
						<Styled.RecipientHeader>
							<Styled.RecipientTitle>Recipient {i + 1}</Styled.RecipientTitle>
							<Styled.RemoveButton
								type="button"
								onClick={() => handleRemoveRecipient(i)}
								aria-label={`Remove Recipient ${i + 1}`}
							>
								<X size={18} strokeWidth={2} />
							</Styled.RemoveButton>
						</Styled.RecipientHeader>
						<Styled.NameRow>
							<PunchupInput label="First Name" name={firstnameId} id={firstnameId} />
							<PunchupInput label="Last Name" name={lastnameId} id={lastnameId} />
						</Styled.NameRow>
						{!noEmail && <PunchupInput label="Attendee Email" name={emailId} id={emailId} />}
						<PunchupInputToggle htmlFor={noEmailId} name={noEmailId} label="Recipient has no email" />
						{assignedSeats.length === 0 ? (
							<Styled.EmptySeats>
								No seats assigned. Select seats above while this recipient is active.
							</Styled.EmptySeats>
						) : (
							<Styled.SeatChips>
								{assignedSeats.map(({ label, ticketTypeName }) => (
									<PunchupTag key={label} label={`${ticketTypeName} · ${label}`} onRemove={() => deselectSeat(label)} />
								))}
							</Styled.SeatChips>
						)}
					</Styled.RecipientCard>
				);
			})}

			<FlexContainer justifyContent="center">
				<PunchupButton type="button" variant="secondary" onClick={handleAddRecipient} text="+  Add recipient" />
			</FlexContainer>
		</>
	);
};
